import { Component, Input, SkipSelf } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CheckoutEventsData } from '@paddle/paddle-js';
import * as Sentry from '@sentry/angular-ivy';
import {
    PlanCardClickEvent,
    USE_CHOOSE_PLAN_INJECTION_TOKEN
} from 'src/app/components/new-billing/plan-card/plan-card.component';
import { ConcretePlan } from 'src/app/pages/new-billing/models/plan.model';
import { PricePeriodicity } from 'src/app/pages/new-billing/types/plan.types';
import { isExplicitPlanSelectionBOrC } from 'src/app/pages/new-billing/utils/explicit-plan-selection.utils';
import { BillingActionsService } from 'src/app/services/billing/billing-actions.service';
import { PlanSelectionService } from 'src/app/services/plan-selection/plan-selection.service';

import { CompanyService } from 'src/app/services/company/company.service';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { ExperimentProps } from 'src/app/services/growthbook/types';
import { TrackingContext } from 'src/app/services/tracking/context';
import { CheckoutV2Component } from '../checkout-v2/checkout-v2.component';

@Component({
  selector: 'app-choose-plan',
  templateUrl: './choose-plan.component.html',
  providers: [
    { provide: USE_CHOOSE_PLAN_INJECTION_TOKEN, useValue: true },
  ],
})
export class ChoosePlanComponent {
  @Input() currentPlan: ConcretePlan;

  @Input() isV2 = false;

  /** When explicit-plan-selection is B or C; alternate Choose Your Plan headings. */
  @Input() useCardEntryExperimentTitle = false;

  paymentRecurrence: PricePeriodicity = 'year';

  constructor(
    private readonly planSelectionService: PlanSelectionService,
    private readonly growthBookService: GrowthBookService,
    private readonly billingActions: BillingActionsService,
    private readonly companyService: CompanyService,
    private readonly router: Router,
    @SkipSelf() private readonly trackingCtx: TrackingContext,
  ) { }


  get canEditBillingDetails(): boolean {
    const customerPlan =
      this.currentPlan?.billingApiData?.plans?.find(plan => plan.planId === this.currentPlan?.billingApiData?.billingDetails?.planId);
    const isQuarterlyOrSemiYearly = customerPlan && ['quarterly', 'semi_yearly'].includes(customerPlan.billingPeriod);
    // Allow editing for canceled subscriptions and scheduled cancellations
    return !this.currentPlan?.subscription?.hasRestrictedSub()
      && this.currentPlan?.billingMode !== 'invoicing'
      && !isQuarterlyOrSemiYearly;
  }

  private async editCardCompleted(paddleEventData: CheckoutEventsData, dialogRef: MatDialogRef<CheckoutV2Component>) {
    const dialogPlanId = dialogRef.componentInstance?.dialogData?.planId;
    const currentPlanId = this.currentPlan?.billingApiData?.billingDetails?.planId;
    if (dialogPlanId && dialogPlanId !== currentPlanId && this.canEditBillingDetails) {
      try {
        await this.companyService.switchCurrentCompanyPlan(dialogRef.componentInstance.dialogData.planId);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'ChoosePlanComponent', method: 'editCardCompleted' },
          extra: {
            dialogPlanId,
            currentPlanId,
            planId: dialogRef.componentInstance.dialogData.planId,
          },
        });
        // Handle error gracefully - you may want to show a user notification here
      }
    }

    this.billingActions.onCardUpdatedCompleted(paddleEventData, dialogRef);
  }
  public handlePlanCardBtnClick({ plan }: PlanCardClickEvent) {
    if (this.isV2) {
      const tdInternal = this.router.parseUrl(this.router.url).queryParams['td_internal'];
      const showTrialHeadsUpBanner = !isExplicitPlanSelectionBOrC(this.growthBookService, tdInternal);
      this.billingActions.openCheckoutV2Dialog({
        currentPlan: plan,
        planId: plan.planId,
        isUpdatingCard: !!plan.paymentModel.cardLast4,
        trackingCtx: { page: 'billing details', section: 'payment method' },
        showTrialHeadsUpBanner,
        checkoutSuccessCallback: this.editCardCompleted.bind(this),
        socialProof: this.shouldShowSocialProofOnBillingPage,
      });
    } else {
      this.planSelectionService.handlePlanSelection(plan, this.shouldShowSocialProofOnBillingPage, this.trackingCtx.context);
    }
  }

  get shouldShowSocialProofOnBillingPage(): boolean {
    const feature = this.growthBookService.getFeatureValue<ExperimentProps>('billing-page-social-proof-exp');
    return feature?.variationid === 'B';
  }
}
